import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const actionIconStyle = { width: 32, height: 32, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 };
const solidRed = { backgroundColor: '#dc3545', borderColor: '#dc3545', color: '#fff', opacity: 1 };
const solidOrange = { backgroundColor: '#fd7e14', borderColor: '#fd7e14', color: '#fff', opacity: 1 };
const solidGreen = { backgroundColor: '#198754', borderColor: '#198754', color: '#fff', opacity: 1 };

function formatDate(v) {
  if (!v) return '—';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [year, month, day] = v.slice(0, 10).split('-');
  return `${day} ${months[Number(month) - 1]} ${year}`;
}

function AdminCertificates() {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const getToken = () => {
    const session = JSON.parse(sessionStorage.getItem('adminSession'));
    return session?.token || null;
  };

  const fetchCourses = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${apiUrl}/api/admin/courses`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setCourses(response.data.courses || []);
    } catch (err) { /* non-fatal */ }
  }, []);

  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) { setError('Authentication required.'); setLoading(false); return; }
      const params = {};
      if (search) params.search = search;
      if (courseFilter) params.courseId = courseFilter;
      if (statusFilter) params.status = statusFilter;
      const response = await axios.get(`${apiUrl}/api/admin/certificates`, { headers: { Authorization: `Bearer ${token}` }, params });
      if (response.data.success) setCertificates(response.data.certificates || []);
      else setError(response.data.message || 'Failed to fetch certificates.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching certificates.');
    } finally {
      setLoading(false);
    }
  }, [search, courseFilter, statusFilter]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);
  useEffect(() => {
    const t = setTimeout(fetchCertificates, 300);
    return () => clearTimeout(t);
  }, [fetchCertificates]);

  const toggleStatus = async (cert) => {
    const newStatus = cert.status === 'active' ? 'revoked' : 'active';
    try {
      const token = getToken();
      await axios.put(`${apiUrl}/api/admin/certificates/${cert.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(`Certificate ${newStatus === 'active' ? 'reactivated' : 'revoked'}.`);
      setTimeout(() => setSuccess(''), 3000);
      fetchCertificates();
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating status.');
    }
  };

  const deleteCertificate = async (cert) => {
    if (!window.confirm(`Delete certificate "${cert.certificate_number}"? This cannot be undone.`)) return;
    try {
      const token = getToken();
      const response = await axios.delete(`${apiUrl}/api/admin/certificates/${cert.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) fetchCertificates();
      else setError(response.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting certificate.');
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li><Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">Home</Link></li>
                <li><span className="text-gray-500 fw-normal d-flex"><i className="ph ph-caret-right" /></span></li>
                <li><span className="text-main-600 fw-normal text-15">Certificates</span></li>
              </ul>
            </div>
            <button type="button" className="btn btn-main rounded-pill py-9" onClick={() => navigate('/admin-certificate-form')}>
              <i className="ph ph-plus me-2" /> Issue Certificate
            </button>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}
          {success && <div className="alert alert-success mb-20">{success}</div>}

          <div className="card mb-24">
            <div className="card-body">
              <div className="row g-16">
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Search</label>
                  <input
                    type="text"
                    className="form-control py-9"
                    placeholder="Certificate number, student name, or ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Course</label>
                  <select className="form-control py-9" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
                    <option value="">All Courses</option>
                    {courses.map((c) => (<option key={c.id} value={c.id}>{c.coursename}</option>))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Status</label>
                  <select className="form-control py-9" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="revoked">Revoked</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header border-bottom border-gray-100">
              <h5 className="mb-0">Certificates ({certificates.length})</h5>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
              ) : certificates.length === 0 ? (
                <div className="text-center py-40 text-gray-400">No certificates found.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead>
                      <tr>
                        <th>Certificate No.</th>
                        <th>Student</th>
                        <th>Course</th>
                        <th>Issued</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {certificates.map((c) => (
                        <tr key={c.id}>
                          <td className="fw-semibold">{c.certificate_number}</td>
                          <td>{c.firstName} {c.lastName} <span className="text-13 text-gray-400">({c.studentid})</span></td>
                          <td style={{ maxWidth: 200, whiteSpace: 'normal' }}>{c.coursename || '—'}</td>
                          <td>{formatDate(c.issued_date)}</td>
                          <td>
                            <span className={`badge ${c.status === 'active' ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'}`}>{c.status}</span>
                          </td>
                          <td>
                            <div className="d-flex gap-6 flex-wrap">
                              <button type="button" className="btn btn-sm btn-outline-main py-6 px-8" style={actionIconStyle} title="Edit" onClick={() => navigate(`/admin-certificate-form/${c.id}`)}>
                                <i className="ph ph-pencil-simple" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm py-6 px-8"
                                style={{ ...actionIconStyle, ...(c.status === 'active' ? solidOrange : solidGreen) }}
                                title={c.status === 'active' ? 'Revoke' : 'Reactivate'}
                                onClick={() => toggleStatus(c)}
                              >
                                <i className={`ph ${c.status === 'active' ? 'ph-prohibit' : 'ph-arrow-counter-clockwise'}`} />
                              </button>
                              <button type="button" className="btn btn-sm py-6 px-8" style={{ ...actionIconStyle, ...solidRed }} title="Delete" onClick={() => deleteCertificate(c)}>
                                <i className="ph ph-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminCertificates;
